import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Stack,
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Skeleton,
  useTheme,
  useMediaQuery,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useToast } from '../hooks/useToast';
import Navbar from '../components/Navigation/Navbar';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';
import socketClient from '../utils/socketClient';
import { apiClient } from '../utils/apiClient';

const formatPrice = (value) => `${new Intl.NumberFormat('en-US').format(value ?? 0)} TND`;

const getCategoryIcon = (category) => {
  const mapping = {
    Electronics: '📱',
    Fashion: '👗',
    'Home & Garden': '🏠',
    Sports: '⚽',
    Books: '📚',
    Toys: '🧸',
    Jewelry: '💎',
    Furniture: '🪑',
    Art: '🎨',
    Collectibles: '🧾',
    Vehicles: '🚗',
  };
  return mapping[category] || '🏛️';
};

const getTimeRemaining = (endTime) => {
  if (!endTime) return null;

  const now = Date.now();
  const end = new Date(endTime).getTime();
  if (Number.isNaN(end) || end <= now) return 'Ended';

  const diff = end - now;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remainder = hours % 24;
    return remainder ? `${days}d ${remainder}h left` : `${days}d left`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m left`;
  }

  return `${minutes}m left`;
};

const getStartsIn = (startTime) => {
  if (!startTime) return null;

  const now = Date.now();
  const start = new Date(startTime).getTime();
  if (Number.isNaN(start)) return null;

  const diff = start - now;
  if (diff <= 0) return 'Starting now';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const RoomsBid = () => {
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const borderRadius = `${theme.shape.borderRadius}px`;
  const compactRadius = `${Math.max(theme.shape.borderRadius - 10, 12)}px`;
  const { token } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const toast = useToast();

  const [liveRooms, setLiveRooms] = useState([]);
  const [upcomingRooms, setUpcomingRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const ensureSocketConnection = useCallback(() => {
    if (!socketClient.socket || !socketClient.isConnected) {
      socketClient.connect(token || undefined);
      return;
    }
    if (token && socketClient.isConnected && !socketClient.isAuthenticated) {
      socketClient.authenticate(token);
    }
  }, [token]);

  useEffect(() => {
    ensureSocketConnection();
  }, [ensureSocketConnection]);

  const categorizeRoom = useCallback((room) => {
    const now = Date.now();
    const start = room?.startTime ? new Date(room.startTime).getTime() : NaN;
    const end = room?.endTime ? new Date(room.endTime).getTime() : NaN;
    const hasValidWindow = Number.isFinite(start) && Number.isFinite(end);
    const isLive = room?.status === 'active' && hasValidWindow && start <= now && end >= now;
    const isUpcoming = room?.status === 'pending' || (!isLive && Number.isFinite(start) && start > now);
    return { isLive, isUpcoming };
  }, []);

  const handleRealtimeRoomUpdate = useCallback((payload) => {
    const updatedRoom = payload?.room;
    if (!updatedRoom?._id) return;

    const normalizedRoom = {
      ...updatedRoom,
      productId: updatedRoom.productId || updatedRoom.product || null
    };
    const { isLive, isUpcoming } = categorizeRoom(normalizedRoom);

    setLiveRooms((prev) => {
      const filtered = prev.filter((room) => room._id !== normalizedRoom._id);
      return isLive ? [normalizedRoom, ...filtered] : filtered;
    });

    setUpcomingRooms((prev) => {
      const filtered = prev.filter((room) => room._id !== normalizedRoom._id);
      return !isLive && isUpcoming ? [normalizedRoom, ...filtered] : filtered;
    });
  }, [categorizeRoom]);

  useEffect(() => {
    socketClient.on('room_update', handleRealtimeRoomUpdate);
    return () => {
      socketClient.off('room_update', handleRealtimeRoomUpdate);
    };
  }, [handleRealtimeRoomUpdate]);

  const resolveParticipationFee = (room) => {
    const fee = Number(
      room?.participationFee ??
      room?.productId?.participationFee ??
      room?.packConfigId?.participationFee ??
      0
    );
    return Number.isFinite(fee) && fee > 0 ? fee : 0;
  };

  const formatParticipationLabel = (room) => {
    const fee = resolveParticipationFee(room);
    return fee > 0 ? formatPrice(fee) : t('roomsBid.free');
  };

  const handleParticipateClick = (room) => {
    if (!token) {
      navigate('/auth');
      return;
    }
    navigate(`/auction-room/${room._id}?mode=participant`);
  };

  const handleViewClick = (room) => {
    navigate(`/auction-room/${room._id}?mode=observer`);
  };

  const fetchLiveRooms = useCallback(async () => {
    try {
      const data = await apiClient.get('/api/auction-rooms', { params: { status: 'active' } });

      if (data.success) {
        const now = Date.now();
        const filtered = (data.data || []).filter((room) => {
          const start = new Date(room.startTime).getTime();
          const end = new Date(room.endTime).getTime();
          return !Number.isNaN(start) && !Number.isNaN(end) && start <= now && end >= now;
        });
        setLiveRooms(filtered);
      } else {
        setLiveRooms([]);
      }
    } catch (error) {
      console.error('Error fetching live rooms:', error);
      setLiveRooms([]);
    }
  }, []);

  const fetchUpcomingRooms = useCallback(async () => {
    try {
      const data = await apiClient.get('/api/auction-rooms', { params: { status: 'pending' } });

      if (data.success) {
        const now = Date.now();
        const filtered = (data.data || []).filter((room) => {
          const start = new Date(room.startTime).getTime();
          return !Number.isNaN(start) && start > now;
        });
        setUpcomingRooms(filtered);
      } else {
        setUpcomingRooms([]);
      }
    } catch (error) {
      console.error('Error fetching upcoming rooms:', error);
      setUpcomingRooms([]);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadRooms = async () => {
      if (!isMounted) return;
      setLoading(true);
      await Promise.all([fetchLiveRooms(), fetchUpcomingRooms()]);
      if (isMounted) {
        setLoading(false);
      }
    };

    loadRooms();

    const interval = setInterval(() => {
      fetchLiveRooms();
      fetchUpcomingRooms();
    }, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [fetchLiveRooms, fetchUpcomingRooms]);

  const handleDeleteRoom = useCallback(
    async (roomId) => {
      if (!hasPermission('manage_auctions')) {
        toast.error(t('roomsBid.noDeletePermission'));
        return;
      }

      // TODO: Replace window.confirm with a proper confirmation dialog component
      if (!window.confirm(t('roomsBid.deleteConfirm'))) {
        return;
      }

      try {
        const data = await apiClient.delete(`/api/auction-rooms/${roomId}`);

        if (data?.success !== false) {
          await Promise.all([fetchLiveRooms(), fetchUpcomingRooms()]);
          toast.success(t('roomsBid.deleteSuccess'));
        } else {
          toast.error(t('roomsBid.deleteFailed'));
        }
      } catch (error) {
        console.error('Error deleting auction room:', error);
        toast.error(t('roomsBid.deleteError'));
      }
    },
    [fetchLiveRooms, fetchUpcomingRooms, hasPermission, token]
  );

  const renderSkeletons = () => (
    <Grid container spacing={3} sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Grid item xs={12} sm={6} lg={4} key={`room-skeleton-${index}`} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ borderRadius, p: 2, width: '100%', maxWidth: 420 }}>
            <Skeleton variant="rounded" height={180} sx={{ borderRadius: compactRadius, mb: 2 }} />
            <Skeleton variant="text" width="60%" sx={{ fontSize: 24 }} />
            <Skeleton variant="text" width="40%" sx={{ fontSize: 18 }} />
            <Skeleton variant="rounded" height={80} sx={{ mt: 2, borderRadius: compactRadius }} />
            <Skeleton variant="rounded" height={40} sx={{ mt: 2, borderRadius: compactRadius }} />
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderRoomImage = (room) => {
    const hasImage = room.productId?.images?.length && room.productId?.images[0]?.thumbnailUrl;

    return (
      <Box
        sx={{
          position: 'relative',
          borderRadius,
          overflow: 'hidden',
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
          pt: '70%',
        }}
      >
        {hasImage ? (
          <Box
            component="img"
            src={room.productId.images[0].thumbnailUrl}
            alt={room.productId?.name || 'Auction product'}
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              inset: 0,
              color: theme.palette.text.secondary,
              fontSize: 42,
            }}
          >
            {getCategoryIcon(room.productId?.category)}
          </Stack>
        )}
      </Box>
    );
  };

  const renderLiveRoomCard = (room) => {
    const participationFee = resolveParticipationFee(room);

    return (
      <Card
        key={room._id}
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius,
          border: '1px solid',
          borderColor: 'divider',
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(145deg, rgba(65,217,171,.10), rgba(23,21,34,.94) 52%, rgba(118,87,255,.10))'
              : 'linear-gradient(145deg, #ffffff 0%, #f6f4ff 100%)',
        }}
      >
        <CardHeader
          avatar={
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? 'success.dark' : 'success.light',
                color: theme.palette.mode === 'dark' ? 'success.contrastText' : 'success.main',
                fontSize: 24,
              }}
            >
              {getCategoryIcon(room.productId?.category)}
            </Avatar>
          }
          action={
            hasPermission('manage_auctions') && (
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Edit auction room">
                  <IconButton
                    component={RouterLink}
                    to={`/auction-rooms/manage?edit=${room._id}`}
                    size="small"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete auction room">
                  <IconButton size="small" onClick={() => handleDeleteRoom(room._id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )
          }
          title={
            <Tooltip title={room.productId?.name || 'Untitled room'}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {room.productId?.name || 'Untitled room'}
              </Typography>
            </Tooltip>
          }
          subheader={
            <Chip
              label={t('roomsBid.live')}
              color="success"
              size="small"
              icon={<FlashOnIcon fontSize="inherit" />}
              sx={{ fontWeight: 600 }}
            />
          }
        />

        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flexGrow: 1 }}>
          {renderRoomImage(room)}

          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
              {room.productId?.description || t('roomsBid.descriptionComingSoon')}
            </Typography>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t('roomsBid.lastBid')}
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {room.currentHighestBid
                    ? formatPrice(room.currentHighestBid.amount)
                    : formatPrice(room.minBid)}
                </Typography>
              </Stack>
              <Stack spacing={0.5} alignItems="flex-end">
                <Typography variant="caption" color="text.secondary">
                  {t('common.participants')}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <PeopleIcon fontSize="small" color="info" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {room.analytics?.participantCount || 0}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t('roomsBid.timeRemaining')}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <AccessTimeIcon fontSize="small" color="warning" />
                  <Typography variant="subtitle2" color="warning.main" fontWeight={600}>
                    {getTimeRemaining(room.endTime)}
                  </Typography>
                </Stack>
              </Stack>
              <Chip
                variant={participationFee > 0 ? 'filled' : 'outlined'}
                color={participationFee > 0 ? 'error' : 'success'}
                label={`Frais de participation : ${formatParticipationLabel(room)}`}
                sx={{ fontWeight: 600 }}
              />
            </Stack>
          </Stack>
        </CardContent>

        <CardActions sx={{ p: 3, pt: 0, gap: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            color={token ? 'success' : 'primary'}
            onClick={() => handleParticipateClick(room)}
            endIcon={<PlayCircleFilledIcon />}
            sx={{ fontWeight: 600 }}
          >
            {token ? t('roomsBid.participate') : t('roomsBid.signInToParticipate')}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="info"
            onClick={() => handleViewClick(room)}
            startIcon={<VisibilityIcon />}
            sx={{ fontWeight: 600 }}
          >
            {t('roomsBid.view')}
          </Button>
        </CardActions>
      </Card>
    );
  };

  const renderUpcomingRoomCard = (room) => {
    const participationFee = resolveParticipationFee(room);

    return (
      <Card
        key={room._id}
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius,
          border: '1px solid',
          borderColor: 'divider',
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(145deg, rgba(118,87,255,.19), rgba(23,21,34,.95) 58%, rgba(255,113,79,.07))'
              : 'linear-gradient(145deg, #ffffff 0%, #f4f1ff 100%)',
        }}
      >
        <CardHeader
          avatar={
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? 'warning.dark' : 'warning.light',
                color: theme.palette.mode === 'dark' ? 'warning.contrastText' : 'warning.main',
                fontSize: 24,
              }}
            >
              {getCategoryIcon(room.productId?.category)}
            </Avatar>
          }
          action={
            hasPermission('manage_auctions') && (
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Edit auction room">
                  <IconButton
                    component={RouterLink}
                    to={`/auction-rooms/manage?edit=${room._id}`}
                    size="small"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete auction room">
                  <IconButton size="small" onClick={() => handleDeleteRoom(room._id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )
          }
          title={
            <Tooltip title={room.productId?.name || t('roomsBid.untitledRoom')}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {room.productId?.name || t('roomsBid.untitledRoom')}
              </Typography>
            </Tooltip>
          }
          subheader={
            <Chip
              label={t('roomsBid.scheduled')}
              color="warning"
              size="small"
              icon={<ScheduleIcon fontSize="inherit" />}
              sx={{ fontWeight: 600 }}
            />
          }
        />

        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flexGrow: 1 }}>
          {renderRoomImage(room)}

          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
              {room.productId?.description || t('roomsBid.descriptionComingSoon')}
            </Typography>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {t('roomsBid.startingBid')}
                </Typography>
                <Typography variant="h6" color="warning.main" fontWeight={700}>
                  {formatPrice(room.minBid)}
                </Typography>
              </Stack>
              <Stack spacing={0.5} alignItems="flex-end">
                <Typography variant="caption" color="text.secondary">
                  {t('roomsBid.startsIn')}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <ScheduleIcon fontSize="small" color="warning" />
                  <Typography variant="subtitle2" color="warning.main" fontWeight={600}>
                    {getStartsIn(room.startTime)}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Chip
              variant={participationFee > 0 ? 'filled' : 'outlined'}
              color={participationFee > 0 ? 'error' : 'success'}
              label={t('roomsBid.participationFee', { fee: formatParticipationLabel(room) })}
              sx={{ fontWeight: 600, alignSelf: 'flex-start' }}
            />
          </Stack>
        </CardContent>

        <CardActions sx={{ p: 3, pt: 0, gap: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            color="warning"
            disabled
            endIcon={<ScheduleIcon />}
            sx={{ fontWeight: 600 }}
          >
            {t('roomsBid.notYetAvailable')}
          </Button>
          <Tooltip title={t('roomsBid.notStartedTooltip')}>
            <Button
              fullWidth
              variant="outlined"
              color="info"
              onClick={() => handleViewClick(room)}
              startIcon={<VisibilityIcon />}
              sx={{ fontWeight: 600 }}
            >
              {t('roomsBid.view')}
            </Button>
          </Tooltip>
        </CardActions>
      </Card>
    );
  };

  const showEmptyState = !loading && liveRooms.length === 0 && upcomingRooms.length === 0;

  return (
    <SciFiBackground>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'transparent',
        }}
      >
        <Navbar />
        <Container maxWidth="xl" sx={{ flex: 1, py: { xs: 4, md: 6 }, px: { xs: 2, sm: 3, md: 4 } }}>
          <Stack spacing={5}>
            <Stack spacing={4}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={4}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Stack spacing={1.5} maxWidth={{ xs: '100%', md: '70%' }}>
                  <Typography variant="h3" fontWeight={800} color="text.primary">
                    {t('roomsBid.title')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t('roomsBid.subtitle')}
                  </Typography>
                </Stack>
                {hasPermission('manage_auctions') && (
                  <Button
                    variant="contained"
                    color="primary"
                    component={RouterLink}
                    to="/auction-rooms/manage"
                    startIcon={<EditIcon />}
                    size={isDownMd ? 'medium' : 'large'}
                    sx={{ fontWeight: 700 }}
                  >
                    {t('roomsBid.createRoom')}
                  </Button>
                )}
              </Stack>
            </Stack>

            {!token && (
              <Alert
                severity="info"
                variant="outlined"
                action={
                  <Button color="inherit" size="small" onClick={() => navigate('/auth')} sx={{ fontWeight: 700 }}>
                    {t('roomsBid.guestBanner.action')}
                  </Button>
                }
                sx={{ borderRadius: compactRadius, alignItems: 'center' }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  {t('roomsBid.guestBanner.title')}
                </Typography>
                <Typography variant="body2">
                  {t('roomsBid.guestBanner.description')}
                </Typography>
              </Alert>
            )}

            {loading && renderSkeletons()}

            {!loading && liveRooms.length > 0 && (
              <Stack spacing={3}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Chip color="success" label="Live" size="small" icon={<PlayCircleFilledIcon fontSize="small" />} />
                  <Typography variant="h5" fontWeight={700}>
                    {t('roomsBid.activeAuctions')}
                  </Typography>
                </Stack>
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                  <Grid container spacing={3}>
                    {liveRooms.map((room) => (
                      <Grid item xs={12} sm={6} md={6} lg={4} key={room._id}>
                        {renderLiveRoomCard(room)}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            )}

            {!loading && upcomingRooms.length > 0 && (
              <Stack spacing={3}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Chip color="warning" label="Scheduled" size="small" icon={<ScheduleIcon fontSize="small" />} />
                  <Typography variant="h5" fontWeight={700}>
                    {t('roomsBid.upcomingRooms')}
                  </Typography>
                </Stack>
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                  <Grid container spacing={3}>
                    {upcomingRooms.map((room) => (
                      <Grid item xs={12} sm={6} md={6} lg={4} key={room._id}>
                        {renderUpcomingRoomCard(room)}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            )}

            {showEmptyState && (
              <Card
                elevation={0}
                sx={{
                  borderRadius,
                  border: '1px dashed',
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(148, 163, 184, 0.4)',
                  background: 'transparent',
                  textAlign: 'center',
                  py: 8,
                  px: { xs: 4, md: 8 },
                }}
              >
                <Stack spacing={3} alignItems="center">
                  <Typography variant="h2">🏛️</Typography>
                  <Stack spacing={1}>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                      {t('roomsBid.noRoomsTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" maxWidth={420}>
                      {t('roomsBid.noRoomsDesc')}
                    </Typography>
                  </Stack>
                  <Alert
                    severity="info"
                    icon={<FlashOnIcon fontSize="small" />}
                    sx={{ maxWidth: 420 }}
                  >
                    {t('roomsBid.autoRefreshNotice')}
                  </Alert>
                </Stack>
              </Card>
            )}
          </Stack>
        </Container>
      </Box>
    </SciFiBackground>
  );
};

export default RoomsBid;
